import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Divider,
  Button,
  Avatar,
  Chip,
  Fade,
  Slide,
  useScrollTrigger
} from '@mui/material';
import {
  Psychology,
  Engineering,
  Security,
  TrendingUp,
  Lightbulb,
  LocationOn,
  Visibility,
  Speed,
  AutoGraph,
  People,
  Architecture,
  Timeline,
  RocketLaunch,
  EmojiObjects,
  HandshakeOutlined,
  VerifiedUser,
  TrendingUpOutlined,
  Warning,
  Insights,
  EmojiObjectsOutlined,
  VerifiedUserOutlined,
  SupportAgent,
  AutoFixHigh,
  Moving,
  Block,
  HealthAndSafety
} from '@mui/icons-material';
import marketingTheme from '../themes/marketingTheme';

const About = () => {
  const [activeSection, setActiveSection] = useState(0);
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  // Enhanced company story with more compelling narrative


  // Company core values
  const values = [
    {
      icon: <EmojiObjectsOutlined sx={{ color: marketingTheme.palette.primary.main, fontSize: 40 }} />,
      title: 'Differentiated Innovation',
      description: 'We have an unrelenting focus on translating novel AI capability into commercial outcomes for our clients. We approach our delivery with continuous mindsets of curiosity and learning. We act with courage and resourcefulness to spark change.'
    },
    {
      icon: <VerifiedUserOutlined sx={{ color: marketingTheme.palette.primary.main, fontSize: 40 }} />,
      title: 'Authenticity',
      description: 'To earn and maintain trust, we always act with sincerity, integrity and honesty. We practice radical transparency in our communication.'
    },
    {
      icon: <SupportAgent sx={{ color: marketingTheme.palette.primary.main, fontSize: 40 }} />,
      title: 'Customer',
      description: 'We only target authentic customer needs and only develop solutions of high quality. We provide quality service, and ensure customer satisfaction.'
    },
    {
      icon: <AutoFixHigh sx={{ color: marketingTheme.palette.primary.main, fontSize: 40 }} />,
      title: 'Make Difficult Seem Simple',
      description: 'We take complex industrial challenges and create elegant, intuitive solutions. Our commitment is to remove complexity from your operations, not add to it.'
    },
    {
      icon: <Moving sx={{ color: marketingTheme.palette.primary.main, fontSize: 40 }} />,
      title: 'Move It Forward',
      description: 'We minimise "wheel spinning" and focus on resolution. We embrace clarity and simplicity. We focus on what matters.'
    }
  ];

  // Team philosophy
  const teamPhilosophy = [
    {
      icon: <People sx={{ color: marketingTheme.palette.primary.main, fontSize: 36 }} />,
      title: 'Cross-Functional Team',
      description: 'Computer vision engineers, lean experts, UX designers, and domain specialists working as one team to solve complex challenges.'
    },
    {
      icon: <Psychology sx={{ color: marketingTheme.palette.primary.main, fontSize: 36 }} />,
      title: 'Curious Minds',
      description: 'We approach every challenge with genuine curiosity and the courage and resourcefulness to spark change.'
    },
    {
      icon: <Speed sx={{ color: marketingTheme.palette.primary.main, fontSize: 36 }} />,
      title: 'Continuous Improvement',
      description: 'We move fast, learn faster, and adapt continuously based on real-world feedback and results.'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#0a0a0f', minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background gradient */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(237, 185, 46, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(46, 125, 237, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(125, 46, 237, 0.1) 0%, transparent 50%)
        `,
        opacity: 0.6
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 12, pb: 8 }}>
        {/* Hero Section - Completely Redesigned */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 16 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white', 
                mb: 4,
                fontSize: { xs: '2rem', md: '3.5rem' },
                fontWeight: 900,
                background: `linear-gradient(135deg, #ffffff 0%, ${marketingTheme.palette.primary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.1
              }}
            >
              Improving Industrial Productivity and Safety
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                mb: 4,
                fontSize: { xs: '1.2rem', md: '1.8rem' },
                fontWeight: 300,
                maxWidth: '800px',
                margin: '0 auto 24px auto',
                lineHeight: 1.6
              }}
            >
              Analysing the invisible patterns in human movement can improve productivity and safety in industrial workplaces.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip 
                label="Vision AI" 
                sx={{ 
                  backgroundColor: 'rgba(237, 185, 46, 0.2)', 
                  color: marketingTheme.palette.primary.main, 
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  padding: '8px 4px'
                }} 
              />
              <Chip 
                label="Spatial Computing" 
                sx={{ 
                  backgroundColor: 'rgba(46, 125, 237, 0.2)', 
                  color: '#87CEEB', 
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  padding: '8px 4px'
                }} 
              />
              <Chip 
                label="Human-Centered Design" 
                sx={{ 
                  backgroundColor: 'rgba(125, 237, 46, 0.2)', 
                  color: '#90EE90', 
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  padding: '8px 4px'
                }} 
              />
            </Box>
          </Box>
        </Fade>

        {/* The Problem We're Solving - New Section */}
        <Slide direction="up" in timeout={1200}>
          <Box sx={{ mb: 16 }}>
            <Typography variant="h2" sx={{ 
              color: 'white', 
              mb: 8, 
              textAlign: 'center', 
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}>
              The Problem We're Solving
            </Typography>
            <Grid container spacing={6}>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1500}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                    border: '1px solid rgba(237, 185, 46, 0.2)',
                    height: '100%',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    '&:hover': {
                      border: '1px solid rgba(237, 185, 46, 0.6)',
                      transform: 'translateY(-8px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 20px 40px rgba(237, 185, 46, 0.1)'
                    }
                  }}>
                    <CardContent sx={{ p: 5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 3 }}>
                        <People sx={{ color: marketingTheme.palette.primary.main, fontSize: 48 }} />
                      </Box>
                      <Typography variant="h5" sx={{ 
                        color: marketingTheme.palette.primary.main, 
                        mb: 3, 
                        fontWeight: 'bold',
                        fontSize: '1.4rem'
                      }}>
                        Human Potential
                      </Typography>
                      <Typography sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        lineHeight: 1.7, 
                        fontSize: '1rem',
                        mb: 3,
                        flex: 1
                      }}>
                        Often constrained by workplace designs that introduce excess movement and fatigue. Many workplace inefficiencies remain invisible and unaddressed. We're driven by the conviction that human potential in industrial settings has been systematically underestimated and under-supported.
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1700}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                    border: '1px solid rgba(237, 185, 46, 0.2)',
                    height: '100%',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    '&:hover': {
                      border: '1px solid rgba(237, 185, 46, 0.6)',
                      transform: 'translateY(-8px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 20px 40px rgba(237, 185, 46, 0.1)'
                    }
                  }}>
                    <CardContent sx={{ p: 5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 3 }}>
                        <Engineering sx={{ color: marketingTheme.palette.primary.main, fontSize: 48 }} />
                      </Box>
                      <Typography variant="h5" sx={{ 
                        color: marketingTheme.palette.primary.main, 
                        mb: 3, 
                        fontWeight: 'bold',
                        fontSize: '1.4rem'
                      }}>
                        Current Reality
                      </Typography>
                      <Typography sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        lineHeight: 1.7, 
                        fontSize: '1rem',
                        mb: 3,
                        flex: 1
                      }}>
                        Industrial environments are not designed around human movement patterns. This introduces fatigue, inefficiency, and heightened risk of injury. We believe in improving the way industrial work is undertaken.
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
              <Grid item xs={12} md={4}>
                <Fade in timeout={1900}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                    border: '1px solid rgba(237, 185, 46, 0.2)',
                    height: '100%',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    '&:hover': {
                      border: '1px solid rgba(237, 185, 46, 0.6)',
                      transform: 'translateY(-8px)',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      boxShadow: '0 20px 40px rgba(237, 185, 46, 0.1)'
                    }
                  }}>
                    <CardContent sx={{ p: 5, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 3 }}>
                        <Insights sx={{ color: marketingTheme.palette.primary.main, fontSize: 48 }} />
                      </Box>
                      <Typography variant="h5" sx={{ 
                        color: marketingTheme.palette.primary.main, 
                        mb: 3, 
                        fontWeight: 'bold',
                        fontSize: '1.4rem'
                      }}>
                        The Opportunity
                      </Typography>
                      <Typography sx={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        lineHeight: 1.7, 
                        fontSize: '1rem',
                        mb: 3,
                        flex: 1
                      }}>
                        AI that comprehends movement patterns and identifies specific workplace inefficiencies. Better understanding of movement can transform workplace design.
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            </Grid>
          </Box>
        </Slide>

        {/* Why Movement Matters - New Section */}
        <Box sx={{ mb: 16 }}>
          <Typography variant="h2" sx={{ 
            color: 'white', 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Why Movement Matters
          </Typography>
          <Typography sx={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            fontSize: '1.2rem', 
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 32px auto'
          }}>
            Every workplace has hidden patterns—movement patterns—where people walk, reach, interact with tools, and perform tasks.
            These patterns reveal inefficiencies that cost time, energy, and wellbeing. 
            We make the invisible visible, transforming work design and layout.
          </Typography>
          {/* <Typography sx={{ 
            color: 'rgba(237, 185, 46, 0.9)', 
            fontSize: '1.1rem', 
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 48px auto',
            fontStyle: 'italic',
            fontWeight: 500
          }}>
            We believe every person deserves to go home from work safely, and to spend their day doing work that is meaningful and purposeful.
          </Typography> */}
          
          <Card sx={{ 
            backgroundColor: 'rgba(237, 185, 46, 0.05)', 
            border: '2px solid rgba(237, 185, 46, 0.3)'
          }}>
            <CardContent sx={{ p: 6 }}>
              <Typography variant="h4" sx={{ 
                color: marketingTheme.palette.primary.main, 
                mb: 4, 
                fontWeight: 'bold', 
                textAlign: 'center' 
              }}>
                The Results
              </Typography>
              <Typography sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '1.3rem', 
                lineHeight: 1.8, 
                textAlign: 'center',
                maxWidth: '1000px',
                margin: '0 auto'
              }}>
                Better workplace layouts reduce walking distances, or excess reaching. Optimised tool placement reduces repositioning time,
                optimised tool setups reduce setup time, and optimised workflows reduce unnecessary steps.
                Eliminating movement bottlenecks prevents fatigue-related errors. 
                Enhancing productivity and safety.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Our Values - Redesigned */}
        <Box sx={{ mb: 16 }}>
          <Typography variant="h2" sx={{ 
            color: 'white', 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} key={index}>
                <Fade in timeout={2000 + index * 150}>
                  <Card sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '2px solid rgba(237, 185, 46, 0.4)',
                    height: '100%',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '16px',
                    '&:hover': {
                      border: '2px solid rgba(237, 185, 46, 0.8)',
                      transform: 'translateY(-8px) scale(1.02)',
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      boxShadow: '0 30px 60px rgba(237, 185, 46, 0.2)'
                    },

                  }}>
                    <CardContent sx={{ p: 6, textAlign: 'left', pt: 8 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 4,
                        gap: 3
                      }}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(237, 185, 46, 0.15)',
                          borderRadius: '50%',
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {value.icon}
                        </Box>
                        <Typography variant="h5" sx={{ 
                          color: marketingTheme.palette.primary.main, 
                          fontWeight: 'bold',
                          fontSize: '1.4rem'
                        }}>
                          {value.title}
                        </Typography>
                      </Box>
                      <Typography sx={{ 
                        color: 'rgba(255, 255, 255, 0.95)', 
                        lineHeight: 1.8, 
                        fontSize: '1.1rem',
                        fontWeight: 400
                      }}>
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How We Work - Updated Section */}
        <Box sx={{ mb: 16 }}>
          <Typography variant="h2" sx={{ 
            color: 'white', 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            How We Work
          </Typography>
          <Grid container spacing={4}>
            {teamPhilosophy.map((philosophy, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.02)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': {
                    border: '1px solid rgba(237, 185, 46, 0.5)',
                    transform: 'translateY(-6px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.06)'
                  }
                }}>
                  <CardContent sx={{ p: 5, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      {philosophy.icon}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      mb: 3, 
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}>
                      {philosophy.title}
                    </Typography>
                    <Typography sx={{ 
                      color: 'rgba(255, 255, 255, 0.85)', 
                      lineHeight: 1.7, 
                      fontSize: '1rem'
                    }}>
                      {philosophy.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Triple Entendre Section - Enhanced */}
        <Box sx={{ mb: 16 }}>
          <Typography variant="h2" sx={{ 
            color: 'white', 
            mb: 6, 
            textAlign: 'center', 
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            Why "MotionLogic"
          </Typography>
          <Typography sx={{ 
            color: 'rgba(255, 255, 255, 0.9)', 
            fontSize: '1.3rem', 
            lineHeight: 1.8,
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto 48px auto'
          }}>
            Our name "MotionLogic" means:
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                border: '1px solid rgba(237, 185, 46, 0.3)',
                height: '100%',
                transition: 'all 0.4s ease',
                position: 'relative',
                '&:hover': {
                  border: '1px solid rgba(237, 185, 46, 0.7)',
                  transform: 'translateY(-8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 20px 40px rgba(237, 185, 46, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 5, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <People sx={{ color: marketingTheme.palette.primary.main, fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    color: marketingTheme.palette.primary.main, 
                    mb: 3, 
                    fontWeight: 'bold',
                    fontSize: '1.4rem'
                  }}>
                    Motion Intelligence
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    lineHeight: 1.7, 
                    fontSize: '1rem' 
                  }}>
                    The paths people, tools, and materials take through industrial spaces. 
                    Understanding these movement patterns reveals opportunities for optimization 
                    and human-centered design.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                border: '1px solid rgba(46, 125, 237, 0.3)',
                height: '100%',
                transition: 'all 0.4s ease',
                position: 'relative',
                '&:hover': {
                  border: '1px solid rgba(46, 125, 237, 0.7)',
                  transform: 'translateY(-8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 20px 40px rgba(46, 125, 237, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 5, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <Visibility sx={{ color: '#2e7ded', fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    color: '#2e7ded', 
                    mb: 3, 
                    fontWeight: 'bold',
                    fontSize: '1.4rem'
                  }}>
                    Visual Logic
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    lineHeight: 1.7, 
                    fontSize: '1rem' 
                  }}>
                    Our AI traverses visual information to understand spatial relationships, 
                    movement patterns, and environmental context. Making sense of video.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                border: '1px solid rgba(125, 237, 46, 0.3)',
                height: '100%',
                transition: 'all 0.4s ease',
                position: 'relative',
                '&:hover': {
                  border: '1px solid rgba(125, 237, 46, 0.7)',
                  transform: 'translateY(-8px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 20px 40px rgba(125, 237, 46, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 5, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <AutoGraph sx={{ color: '#7ded2e', fontSize: 50 }} />
                  </Box>
                  <Typography variant="h5" sx={{ 
                    color: '#7ded2e', 
                    mb: 3, 
                    fontWeight: 'bold',
                    fontSize: '1.4rem'
                  }}>
                    Data Logic
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    lineHeight: 1.7, 
                    fontSize: '1rem' 
                  }}>
                    Information flows through our platform's network of sensors, algorithms, 
                    and interfaces. Data traverses multiple processing stages to become 
                    actionable workplace intelligence.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Vision for the Future - Enhanced */}
        <Box sx={{ mb: 16 }}>
          <Typography variant="h2" sx={{ 
            color: 'white', 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            The Future We're Creating
          </Typography>
          
          <Card sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.03)', 
            border: '2px solid rgba(237, 185, 46, 0.4)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(237, 185, 46, 0.1) 0%, rgba(46, 125, 237, 0.1) 100%)',
              opacity: 0.3
            }} />
            <CardContent sx={{ p: 8, position: 'relative', zIndex: 1 }}>
              <Typography sx={{ 
                color: 'rgba(255, 255, 255, 0.95)', 
                lineHeight: 1.8, 
                fontSize: '1.4rem', 
                mb: 6,
                textAlign: 'center',
                fontWeight: 300
              }}>
                We stand at the threshold of industrial transformation. 
                Creating workplaces where technology amplifies human potential. 
                Where every movement is purposeful, high-touch workflows are optimised,
                and work design is intuitive.
              </Typography>
              
              <Grid container spacing={6} sx={{ textAlign: 'center' }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 4 }}>
                    <Architecture sx={{ color: marketingTheme.palette.primary.main, fontSize: 60, mb: 2 }} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    color: marketingTheme.palette.primary.main, 
                    fontWeight: 'bold', 
                    mb: 3,
                    fontSize: '1.6rem'
                  }}>
                    Intelligent Environments
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: '1.1rem', 
                    lineHeight: 1.7
                  }}>
                    Workplaces that make sense of human behavior patterns and adapt in real-time. 
                    Spaces and tools designed around how people naturally move and work.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 4 }}>
                    <Timeline sx={{ color: marketingTheme.palette.primary.main, fontSize: 60, mb: 2 }} />
                  </Box>
                  <Typography variant="h4" sx={{ 
                    color: marketingTheme.palette.primary.main, 
                    fontWeight: 'bold', 
                    mb: 3,
                    fontSize: '1.6rem'
                  }}>
                    Continuous Optimization
                  </Typography>
                  <Typography sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    fontSize: '1.1rem', 
                    lineHeight: 1.7
                  }}>
                    Systems that learn from every interaction, getting smarter about supporting 
                    human performance. Where workplace design evolves based on real movement 
                    data and human feedback.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Company Identity Section */}
        <Box sx={{ mb: 16 }}>
          <Card sx={{ 
            backgroundColor: 'rgba(237, 185, 46, 0.05)', 
            border: '2px solid rgba(237, 185, 46, 0.3)',
            borderRadius: '16px',
            transition: 'all 0.4s ease',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              border: '2px solid rgba(237, 185, 46, 0.5)',
              transform: 'translateY(-4px)',
              backgroundColor: 'rgba(237, 185, 46, 0.08)',
              boxShadow: '0 20px 40px rgba(237, 185, 46, 0.15)'
            }
          }}>
            <CardContent sx={{ p: 8, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ 
                fontWeight: 'bold',
                color: marketingTheme.palette.primary.main,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}>
                MotionLogic
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 400,
                fontSize: { xs: '1.2rem', md: '1.5rem' }
              }}>
                MotionLogic&reg; - Improving Industrial Productivity and Safety
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Governance Section */}
        <Box sx={{ mb: 8, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ 
            p: 3, 
            backgroundColor: 'rgba(255, 255, 255, 0.05)', 
            borderRadius: '8px',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography variant="h6" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              fontSize: '1rem', 
              fontWeight: 600, 
              mb: 1 
            }}>
              Governance
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              mb: 1 
            }}>
              If you have concerns regarding misconduct, safety, or legal compliance, please contact our confidential inbox: Email: <a href="mailto:integrity@motionlogic.io" style={{ color: 'inherit' }}>integrity@motionlogic.io</a>
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              fontStyle: 'italic',
              display: 'block'
            }}>
              All reports are treated with strict confidentiality and can be submitted anonymously.
            </Typography>
          </Box>
        </Box>
      </Container>

    </Box>
  );
};

export default About;
